import React, { useCallback } from 'react'

import { useRouter } from 'next/router'
import { HiSparkles } from 'react-icons/hi2'

import {
  CompanySearchVisual,
  NewsSearchVisual,
  PeopleSearchVisual,
  ResearchVisual,
} from '@/components/announcements/DiscoveryIntroModal/visuals'
import {
  ShimmerCtaButton,
  StepDots,
  useIntroWizard,
  WizardContinueButton,
} from '@/components/announcements/IntroWizard'
import type { AnnouncementBodyProps } from '@/components/announcements/types'
import { savePendingChatPrefill } from '@/components/chat-v2/lib/localStorage'
import { DialogTitle } from '@/components/common/Dialog'

import { cn } from '@/helpers/shadcn/utils'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'

// Runnable as-is (no placeholders to edit — those stall the handoff) and
// broad enough to be relevant to any customer, whatever they sell.
const TRY_IT_PROMPT =
  'What are the biggest shifts in federal contracting this year, and what do they mean for contractors like us?'

const STEPS = [
  {
    title: 'Sweetspot can search any company',
    body: 'Ask in plain English and Sweetspot searches live company websites and public web profiles, then returns relevant companies with current descriptions and source links you can check.',
    Visual: CompanySearchVisual,
  },
  {
    title: 'Sweetspot can find the right people',
    body: 'Point Sweetspot at an agency or company and it surfaces the contracting officers, program managers, and decision-makers, with roles, org context, and a way to reach them.',
    Visual: PeopleSearchVisual,
  },
  {
    title: 'Sweetspot can surface the news that matters',
    body: 'Ask about a company, market, person, or event and Sweetspot searches current coverage across major publications, trade press, and niche outlets, then ranks the most relevant stories with dates and source links.',
    Visual: NewsSearchVisual,
  },
  {
    title: 'Meet Sweetspot deep research',
    body: 'Give it any complex research task: a company, a person, a market. It breaks the question into subtasks, researches each across the live web, and returns a brief with every claim grounded in citations.',
    Visual: ResearchVisual,
  },
]

/**
 * The discovery launch wizard (company search / people search / news / deep
 * research), ported from Philip's approved "Discovery Onboarding Modal"
 * mockup. Funnels into a prefilled (not auto-sent) research prompt in a new
 * chat. Rendered inside the shared AnnouncementModal, so the single close
 * button comes from the dialog chrome.
 */
export const DiscoveryIntroModal: React.FC<AnnouncementBodyProps> = ({
  data,
  onDismiss,
}) => {
  const router = useRouter()
  const { activeOrgId } = useActiveOrgId()
  const { step, isLast, enterClass, handleNext, handleBack } = useIntroWizard(
    STEPS.length,
  )

  const handleTryItNow = useCallback(() => {
    if (activeOrgId) {
      savePendingChatPrefill({ content: TRY_IT_PROMPT, tool: 'deep_research' })
      void router.push(`/orgs/${activeOrgId}/c/new`)
    }
    onDismiss()
  }, [activeOrgId, router, onDismiss])

  const handleChangelog = useCallback(() => {
    void router.push(
      data.entry_id ? `/changelog?entry=${data.entry_id}` : '/changelog',
    )
    onDismiss()
  }, [data.entry_id, router, onDismiss])

  const active = STEPS[step]
  const Visual = active.Visual

  const backButton = (
    <button
      type="button"
      onClick={handleBack}
      className="h-10 rounded-[9px] px-3 text-[13px] font-medium text-primary-400 transition hover:bg-primary-100 hover:text-primary-900 dark:text-primary-500 dark:hover:bg-primary-800 dark:hover:text-primary-100"
    >
      Back
    </button>
  )

  return (
    <div>
      <div className="relative h-[194px] overflow-hidden border-b border-primary-200 bg-gradient-to-b from-accent-400/15 via-violet-500/[0.06] to-transparent dark:border-primary-800">
        <div key={step} className={cn('absolute inset-0', enterClass)}>
          <Visual />
        </div>
      </div>

      <div className="px-[26px] pb-1.5 pt-[22px]">
        <div key={step} className={enterClass}>
          <DialogTitle className="text-[21px] font-bold leading-[1.22] tracking-[-0.02em] text-primary-900 dark:text-primary-100">
            {active.title}
          </DialogTitle>
          <p className="mt-2 text-sm leading-relaxed text-primary-600 dark:text-primary-300">
            {active.body}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 px-[26px] pb-[22px] pt-5">
        <StepDots count={STEPS.length} step={step} />

        {isLast ? (
          <div className="flex items-center gap-1">
            {backButton}
            <button
              type="button"
              onClick={handleChangelog}
              className="mr-1 h-10 rounded-[10px] border border-primary-300 px-4 text-[13px] font-medium text-primary-600 transition hover:bg-primary-100 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-800"
            >
              Changelog
            </button>
            <ShimmerCtaButton
              onClick={handleTryItNow}
              className="h-10 border border-violet-700 px-[22px] dark:border-violet-600"
            >
              <HiSparkles className="h-4 w-4 shrink-0" />
              Try it now
            </ShimmerCtaButton>
          </div>
        ) : (
          <div className="flex items-center gap-2.5">
            {step > 0 ? backButton : null}
            <WizardContinueButton onClick={handleNext} />
          </div>
        )}
      </div>
    </div>
  )
}
